import { useCallback, useMemo } from 'react';

import { useIsAbTestAvailable } from 'src/hooks/useIsAbTestAvailable';
import { useListAbTestConfigsQuery } from 'src/services/agent-ab-test/agentAbTestApi';
import { useGetAllAutoflowVersionHistoriesV2Query } from 'src/services/workflowBuilderAutoflowApi.ts/workflowBuilderAutoflowApi';

export interface AbTestEligibility {
  disabled: boolean;
  disabledReason: string | undefined;
}

interface UseAbTestEligibilityReturn {
  getEligibility: (workflowId: string) => AbTestEligibility;
  isLoading: boolean;
}

const ELIGIBLE: AbTestEligibility = {
  disabled: false,
  disabledReason: undefined,
};

/**
 * Reusable hook that checks whether a workflow is eligible to create an A/B test.
 *
 * Checks three conditions (extracted from AgentVariantsSection):
 * 1. Workflow already has running A/B test
 * 2. Workflow has insufficient version history (≤1 versions)
 * 3. Workflow has no published version
 *
 * Callers provide the channel-specific workflow ID (via getWorkflowIdByChannel).
 */
export const useAbTestEligibility = (): UseAbTestEligibilityReturn => {
  const isAbTestAvailable = useIsAbTestAvailable();

  const { data: abTestsData } = useListAbTestConfigsQuery(
    {},
    { skip: !isAbTestAvailable },
  );

  const { data: allVersionHistories, isLoading: isLoadingVersionHistories } =
    useGetAllAutoflowVersionHistoriesV2Query(undefined, {
      skip: !isAbTestAvailable,
    });

  const activeAbTestWorkflowIds = useMemo(() => {
    const blockingTests =
      abTestsData?.ab_test_configs.filter(
        config => config.status === 'running' && config.intent_workflow_id,
      ) || [];
    return new Set(
      blockingTests.map(config => config.intent_workflow_id as string),
    );
  }, [abTestsData]);

  const getEligibility = useCallback(
    (workflowId: string): AbTestEligibility => {
      if (!isAbTestAvailable) {
        return {
          disabled: true,
          disabledReason: 'A/B test feature is not enabled',
        };
      }

      if (!workflowId) {
        return {
          disabled: true,
          disabledReason: 'Workflow ID is not available',
        };
      }

      if (activeAbTestWorkflowIds.has(workflowId)) {
        return {
          disabled: true,
          disabledReason: 'Already has an active A/B test',
        };
      }

      const versionHistory = allVersionHistories?.[workflowId] ?? [];

      if (versionHistory.length <= 1) {
        return {
          disabled: true,
          disabledReason: 'No version history available',
        };
      }

      if (!versionHistory.some(v => v.is_published)) {
        return {
          disabled: true,
          disabledReason: 'No published version',
        };
      }

      return ELIGIBLE;
    },
    [isAbTestAvailable, activeAbTestWorkflowIds, allVersionHistories],
  );

  return {
    getEligibility,
    isLoading: isAbTestAvailable && isLoadingVersionHistories,
  };
};
